// matcher:start
/** Published Explore templates scored against one user message. Only a short ranked page enters the model prompt. */

export type ExploreCatalogItem = {
  id: string;
  title: string;
  description?: string | null;
};

export type ExploreMatch = ExploreCatalogItem & {
  score: number;
  /** The title itself is in the message, so the offer does not depend on the model agreeing. */
  phrase: boolean;
};

const STOP = new Set([
  'de', 'da', 'do', 'das', 'dos', 'a', 'o', 'e', 'em', 'um', 'uma', 'para', 'pra', 'com', 'no', 'na', 'nos', 'nas',
  'que', 'se', 'eu', 'quero', 'queria', 'fazer', 'faz', 'video', 'videos', 'gere', 'gerar', 'gera', 'crie', 'criar',
  'cria', 'tipo', 'como', 'exemplo', 'modelo', 'por', 'favor', 'esse', 'essa', 'isso', 'aquilo', 'meu', 'minha',
  'the', 'and', 'with', 'for', 'video',
]);

export function foldMatchText(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

function significantTokens(value: string): string[] {
  return foldMatchText(value).split(' ').filter((token) => token.length >= 3 && !STOP.has(token));
}

function titlePhrase(title: string): { phrase: string; tokens: string[] } | null {
  const tokens = significantTokens(title);
  if (tokens.length >= 2) return { phrase: tokens.join(' '), tokens };
  if (tokens.length === 1 && tokens[0].length >= 6) return { phrase: tokens[0], tokens };
  return null;
}

function descriptionBigrams(description: string): string[] {
  const parts = significantTokens(description);
  const out: string[] = [];
  for (let i = 0; i < parts.length - 1; i++) {
    if (parts[i].length >= 4 && parts[i + 1].length >= 4) out.push(`${parts[i]} ${parts[i + 1]}`);
  }
  return out;
}

function sharedPrefixLength(a: string, b: string): number {
  const n = Math.min(a.length, b.length);
  let i = 0;
  while (i < n && a[i] === b[i]) i += 1;
  return i;
}

/** Exact token, or the same stem ("cinematica" / "cinematografica"). Short words stay exact. */
function tokenHit(needle: string, hay: string[]): 'exact' | 'stem' | null {
  if (hay.includes(needle)) return 'exact';
  if (needle.length < 6) return null;
  for (const token of hay) {
    if (token.length < 6) continue;
    const share = sharedPrefixLength(needle, token);
    const shorter = Math.min(needle.length, token.length);
    if (share >= 6 && share >= shorter - 3) return 'stem';
  }
  return null;
}

/** How many published templates the lexical scan should still read itself. Above this, rank in Postgres and embed when the lexical score is weak. */
export const EXPLORE_LEXICAL_CATALOG = 400;

export const EXPLORE_SEARCH_LIMIT = 8;

/**
 * Lexical rank of one template against the user's sentence.
 * The full catalog stays out of the model prompt; callers keep only the top hits.
 */
export function scoreExploreTemplate(message: string, item: ExploreCatalogItem): ExploreMatch | null {
  const text = foldMatchText(message);
  if (text.length < 4) return null;
  const title = titlePhrase(item.title || '');
  if (!title) return null;
  const queryTokens = significantTokens(message);
  let score = 0;
  const phrase = text.includes(title.phrase);
  if (phrase) score += 100 + Math.min(40, title.phrase.length);
  let titleHits = 0;
  for (const token of title.tokens) {
    const hit = tokenHit(token, queryTokens);
    if (hit === 'exact') {
      if (!phrase) score += token.length >= 6 ? 36 : 8;
      titleHits += 1;
    } else if (hit === 'stem') {
      score += 28;
      titleHits += 1;
    }
  }
  const titleCovered = titleHits === title.tokens.length && title.tokens.length >= 1;
  if (!phrase && titleCovered && title.tokens.length >= 2) score += 24;
  if (descriptionBigrams(item.description || '').some((pair) => text.includes(pair))) score += 50;
  let described = 0;
  for (const token of significantTokens(item.description || '')) {
    const hit = tokenHit(token, queryTokens);
    if (hit === 'exact') described += token.length >= 4 ? 8 : 6;
    else if (hit === 'stem') described += 4;
  }
  score += Math.min(32, described);
  if (score < 36) return null;
  return { ...item, score, phrase: phrase || (titleCovered && title.tokens.length >= 2) };
}

/** Best matches from a catalog page. The page may be the whole catalog or a Postgres prefilter. */
export function searchExploreCatalog(message: string, catalog: ExploreCatalogItem[], limit = EXPLORE_SEARCH_LIMIT): ExploreMatch[] {
  return catalog
    .map((item) => scoreExploreTemplate(message, item))
    .filter((hit): hit is ExploreMatch => Boolean(hit))
    .sort((a, b) => b.score - a.score || a.title.localeCompare(b.title))
    .slice(0, Math.max(1, limit));
}

/** A title match clearly ahead of the next hit can be offered without another guess. */
export function decisiveExploreHit(hits: ExploreMatch[]): ExploreMatch | null {
  const best = hits[0];
  if (!best?.phrase) return null;
  const second = hits[1]?.score || 0;
  if (best.score - second < 12) return null;
  return best;
}

/**
 * When any published row already has a vector, always embed the query.
 * Otherwise embed once the catalog is large, or the lexical winner is weak.
 * Rows still without an embedding stay on the lexical path inside search_video_templates.
 */
export function shouldEmbedExploreQuery(publishedCount: number, bestScore: number, embeddedCount = 0): boolean {
  if (embeddedCount > 0) return true;
  if (publishedCount >= EXPLORE_LEXICAL_CATALOG) return true;
  return publishedCount >= 80 && bestScore < 48;
}

/**
 * One strong template, or nothing. A title phrase beats a description pair.
 * Two unrelated templates at the same strength are skipped so the agent does not guess.
 */
export function matchExploreTemplate(message: string, catalog: ExploreCatalogItem[]): ExploreMatch | null {
  const hits = searchExploreCatalog(message, catalog, 2);
  if (!hits.length) return null;
  if (hits[1] && hits[1].score === hits[0].score) return null;
  return hits[0];
}

/** The person refused the example and wants the normal script. */
export function declinedExploreTemplate(message: string): boolean {
  return /n[aã]o\s+(?:usar|quero)\s+o\s+modelo|sem\s+o\s+modelo|fluxo\s+normal|seguir\s+com\s+o\s+roteiro/i.test(message);
}
// matcher:end

export type FlowSlot = { key: string; slot_type: string; sort_order: number };

function slotRank(slot: FlowSlot): number {
  const group = slot.slot_type === 'image_start' ? 0 : slot.slot_type === 'image_end' ? 2 : slot.slot_type === 'text' ? 9 : 1;
  return group * 1000 + (Number(slot.sort_order) || 0);
}

/** Image slots in play order: opening frame, references, closing frame. */
export function orderedImageSlots<T extends FlowSlot>(slots: T[]): T[] {
  return slots.filter((slot) => slot.slot_type !== 'text').slice().sort((a, b) => slotRank(a) - slotRank(b));
}

/** First flow photos fill the template's image slots. Photos past the last slot stay unassigned. */
export function assignFlowImages(slots: FlowSlot[], urls: string[]): { seeded: Record<string, string>; extra: string[] } {
  const images = orderedImageSlots(slots);
  const seeded: Record<string, string> = {};
  const clean = urls.map((url) => url.trim()).filter(Boolean);
  clean.forEach((url, index) => {
    const slot = images[index];
    if (slot) seeded[slot.key] = url;
  });
  return { seeded, extra: clean.slice(images.length) };
}

export type TemplateLockFlags = {
  model?: boolean;
  resolution?: boolean;
  duration?: boolean;
  audio?: boolean;
  aspect?: boolean;
};

/** The ratio used for this render stays on the board. Model, duration and sound freeze only when the template locks them. */
export function templateLocks(lockModel: boolean, resolutionCount: number): TemplateLockFlags {
  return {
    aspect: true,
    ...(lockModel ? {
      model: true,
      duration: true,
      audio: true,
      resolution: resolutionCount <= 1,
    } : {}),
  };
}
